import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebase/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';

export interface WalletData {
  id: string;
  leagueId: string;
  seasonId: number;
  type: 'main' | 'arena';
  available: number;
  purchased: number;
  gifted: number;
  earned: number;
  claimed: number[];
}

export const useUserWallets = () => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setWallets([]);
      setLoading(false);
      return;
    }

    const ref = collection(db, 'users', user.uid, 'wallets');

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const next: WalletData[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          return {
            id: docSnap.id,
            leagueId: String(data.leagueId ?? ''),
            seasonId: Number(data.seasonId ?? 0),
            type: (data.type ?? 'main') as 'main' | 'arena',

            available: Number(data.available ?? 0),
            purchased: Number(data.purchased ?? 0),
            gifted: Number(data.gifted ?? 0),
            earned: Number(data.earned ?? 0),

            claimed: Array.isArray(data.claimed) ? data.claimed : [],
          };
        });

        setWallets(next);
        setLoading(false);
      },
      (error) => {
        console.error('useUserWallets ERROR:', error);
        setWallets([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user]);

  const getWallet = useMemo(() => {
    return (leagueId: string, seasonId: string, type: 'main' | 'arena') => {
      const wallet = wallets.find(
        (w) =>
          w.leagueId === leagueId &&
          String(w.seasonId) === String(seasonId) &&
          w.type === type,
      );

      return wallet;
    };
  }, [wallets]);

  return {
    wallets,
    loading,
    getWallet,
  };
};

export default useUserWallets;
